import React from "react"
import { GetServerSideProps } from "next"
import { InstantSearch } from "react-instantsearch-dom"
import { findResultsState } from "react-instantsearch-dom/server"
import { InstantSearchProps } from "react-instantsearch-core"

import NotFoundPage from "pages/404"
import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"
import AlgoliaSearchUI from "components/Algolia/AlgoliaSearchUI"
import VirtualRefinements from "@secureo/common/components/Algolia/VirtualRefinements"

import useAlgoliaURLSync from "@secureo/common/hooks/useAlgoliaURLSync"

import { parseAlgoliaSSRRequest } from "@secureo/common/utils/algolia/getInitialSearchState"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchBrandCategory from "@secureo/common/utils/commerceTools/graphql/queries/fetchBrandCategory"
import getCategoryPreviewProps, {
	CategoryPageFindSearchStateProps,
} from "@secureo/common/utils/commerceTools/getCategoryPreviewProps"
import serializeJSON from "@secureo/common/utils/serializeJSON"

import searchClient from "@secureo/common/utils/algolia/searchClient"
import { brandPageFilters } from "config/filterConfig"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import { supportedValidLocales } from "@secureo/common/utils/i18n/supportedLocalesAndCurrencyCodes"

const ancestorBreadCrumbs: BreadCrumb[] = [
	{
		label: "Marken",
		href: `/brands/`,
	},
]

const BrandPage = (props: Props & InstantSearchProps) => {
	const {
		category: brandCategory,
		locale,
		indexName,
		resultsState,
		initialSearchState = {},
		// eslint-disable-next-line
		onSearchParameters = () => {},
		virtualRefinements,
		widgetsCollector,
		slug,
		isFindResultsStateRenderMode = false,
	} = props

	const asPath = `/${locale}/brands/${slug}`
	const [searchState, onSearchStateChange] = useAlgoliaURLSync(
		initialSearchState,
		"/[locale]/brands/[slug]",
		asPath,
	)

	if (!brandCategory) return <NotFoundPage />

	const { name, description, shortDescription, metaTitle, metaDescription } = brandCategory

	const currentPath: BreadCrumb = {
		label: name,
		href: `/brands/${slug}`,
	}

	return (
		<>
			<SEO asPath={asPath} title={metaTitle} description={metaDescription} />
			<InstantSearch
				indexName={indexName}
				searchClient={searchClient}
				resultsState={resultsState}
				searchState={searchState}
				onSearchParameters={onSearchParameters}
				onSearchStateChange={onSearchStateChange}
				widgetsCollector={widgetsCollector}
			>
				<VirtualRefinements virtualRefinements={virtualRefinements} />
				{!isFindResultsStateRenderMode && (
					<BreadCrumbs
						locale={locale}
						currentPath={currentPath}
						ancestors={ancestorBreadCrumbs}
					/>
				)}
				<AlgoliaSearchUI
					locale={locale}
					indexName={indexName}
					filters={brandPageFilters}
					title={name}
					shortDescription={shortDescription}
					description={description}
					virtualRefinements={virtualRefinements}
				/>
			</InstantSearch>
		</>
	)
}

interface Props extends CategoryPageFindSearchStateProps {
	resultsState: InstantSearchProps["resultsState"]
	widgetsCollector?: any
	isFindResultsStateRenderMode?: boolean
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { req, res, params } = context

	const rawLocale = params?.locale as string
	const { locale, language, countryCode } = normalizeLocale(rawLocale)
	const { actualSlug, unsortedIndexName, searchState } = parseAlgoliaSSRRequest(
		req.url,
		params,
		"DZN",
		locale,
	)

	if (!supportedValidLocales.includes(locale)) {
		res.statusCode = 404
		return {
			props: {
				statusCode: 404,
				error: "Locale not supported",
			},
		}
	}

	const accessToken = await anonymousAccessTokenCache()
	const brandCategory = await fetchBrandCategory(accessToken, actualSlug, locale)
	if (!brandCategory) {
		res.statusCode = 404
		return {
			props: {
				statusCode: 404,
				error: "Category not found",
			},
		}
	}

	const previewProps = await getCategoryPreviewProps(
		brandCategory,
		searchState,
		unsortedIndexName,
		accessToken,
		locale,
		language,
		countryCode,
		"DZN",
		"brand",
	)

	let resultsState: InstantSearchProps["resultsState"] = {}
	try {
		resultsState = await findResultsState(
			(props: any) => <BrandPage {...previewProps} {...props} isFindResultsStateRenderMode />,
			{
				indexName: previewProps.indexName,
				searchClient,
				searchState,
			},
		)
	} catch (err) {
		console.error(err)
		res.statusCode = 500
		return {
			props: {
				statusCode: 500,
				error: "Failed to create resultsState",
			},
		}
	}
	const serializedResultsState = serializeJSON(resultsState)

	const props: Props = {
		...previewProps,
		resultsState: serializedResultsState,
	}

	return {
		props,
	}
}

export default BrandPage
